import axios from 'axios';
import AuthApiRoutes from './../constants/AuthApiRoutes';
import {
    fetchingInvitation,
    fetchInvitationSuccess,
    fetchInvitationFailed,
    fetchingInvitations,
    fetchInvitationsSuccess,
    fetchInvitationsFailed,
    fetchingUserClients,
    fetchingUsers,
    fetchUserClientsFailed,
    fetchUserClientsSuccess,
    fetchUsersFailed,
    fetchUsersSuccess,
    reinvitingUser,
    reinviteUserSuccess,
    reinviteUserFailed,
    approvingInvitation,
    approvingInvitationSuccess,
    approvingInvitationFailed,
    bulkApprovingInvitations,
    bulkApproveInvitationsSuccess,
    bulkApproveInvitationsFailed,
    bulkReinvitingUsers,
    bulkReinvitingUsersSuccess,
    bulkReinvitingUsersFailed,
    settingFederationId,
    setFederationIdSuccess,
    setFederationIdFailure,
    creatingInvitations,
    creatingInvitationsSuccess,
    creatingInvitationsFailed,
    linkingInvitations,
    linkInvitationSuccess,
    linkInvitationFailure,
    removingUser,
    removingUserSuccess,
    removingUserFailed,
    settingInactiveDate,
    setInactiveDateSuccess,
    setInactiveDateFailure,
    updatingInvitation,
    updateInvitationSuccess,
    updateInvitationFailure
} from './../actions/AuthActions';

import { getHeaders } from './CommonFunctions';

export {
    fetchInvitation, fetchInvitations, fetchUsers, fetchUserClients,
    setFederationId, createInvitations,
    reinviteUser, linkInvitationPatch, linkInvitationPost, approveInvitation, bulkApproveInvitation,
    bulkReinviteUsers, setInactiveDate, updateInvitation, removeUserFromClient
};

const getApiError = err => {
    let error = {};
    if (err?.response?.data) {
        error = {
            code: err.response.status,
            message: err.response.data.message
        };
    } else {
        error = { code: 500, message: 'Unknown error occurred' };
    }
    return error;
};

function fetchInvitations() {
    const headers = getHeaders();
    return async dispatch => {
        let invitations = [];
        const filter = 'status ne \'Approved\'';
        const top = 100;
        let skip = 0;
        let nextSet = null;
        let error = null;
        dispatch(fetchingInvitations());
        while(nextSet && nextSet.length === top || skip === 0) {
            const url = `${AuthApiRoutes.INVITATIONS}/back-office?$orderby=lastName&$skip=${skip}&$top=${top}&$filter=${filter}`;
            try {
                await axios.get(url, { headers }).then(response => {
                    nextSet = response.data;
                    skip += top;
                    invitations = [...invitations, ...nextSet];
                });
            } catch (err) {
                error = getApiError(err);
                dispatch(fetchInvitationsFailed(error));
            }
        }
        if (!error)
            dispatch(fetchInvitationsSuccess(invitations));
    };
}

function fetchUsers() {
    const headers = getHeaders();
    return async dispatch => {
        dispatch(fetchingUsers());
        let users = [];
        const top = 100;
        let skip = 0;
        let nextSet = null;
        let error = null;
        while(nextSet && nextSet.length === top || skip === 0) {
            const url = `${AuthApiRoutes.USERS}?$orderby=lastName&$skip=${skip}&$top=${top}`;
            try {
                await axios.get(url, { headers }).then(response => {
                    nextSet = response.data;
                    skip += top;
                    users = [...users, ...nextSet];
                });
            } catch (err) {
                error = getApiError(err);
                dispatch(fetchUsersFailed(error));
            }
        }
        if (!error)
            dispatch(fetchUsersSuccess(users));
    };
}

function fetchUserClients() {
    return async dispatch => {
        const headers = getHeaders();
        const url = AuthApiRoutes.USER_CLIENTS;
        dispatch(fetchingUserClients());
        return await axios
            .get(url, { headers })
            .then(response => {
                dispatch(
                    fetchUserClientsSuccess(
                        response.data && response.data.userClients
                            ? response.data.userClients
                            : response.data
                    )
                );
            })
            .catch(err => {
                const error = getApiError(err);
                dispatch(fetchUserClientsFailed(error));
            });
    };
}

function setFederationId(userGuid, clientId, callback) {
    return dispatch => {
        const headers = { headers: getHeaders() };
        const url = `${AuthApiRoutes.USERS}/${userGuid}/switch-salesforce-client-context`;
        const body = JSON.stringify({ clientId });
        dispatch(settingFederationId());
        axios
            .post(url, body, headers)
            .then(() => {
                dispatch(setFederationIdSuccess());
                callback(true);
            })
            .catch(error => {
                dispatch(setFederationIdFailure(error));
                callback(false);
            })
    };
}

function linkInvitationPatch(invitationGuid, emailAddress, password) {
    return dispatch => {
        const url = `${AuthApiRoutes.BASE}/invitations/${invitationGuid}/link`;
        const body = {
            emailAddress: emailAddress,
            password: password
        };
        dispatch(linkingInvitations());

        axios
            .patch(url, body)
            .then(() => {
                dispatch(linkInvitationSuccess());
            })
            .catch(error => {
                dispatch(linkInvitationFailure(error));
            });
    };
}

function linkInvitationPost(invitationGuid, emailAddress, password, firstName, lastName) {
    return dispatch => {
        const url = `${AuthApiRoutes.BASE}/invitations/${invitationGuid}/link`;
        const body = {
            emailAddress,
            password,
            firstName,
            lastName
        };
        dispatch(linkingInvitations());

        axios
            .post(url, body)
            .then(() => {
                dispatch(linkInvitationSuccess());
            })
            .catch(error => {
                dispatch(linkInvitationFailure(error));
            });
    };
}

function createInvitations(invitations) {
    return dispatch => {
        const headers = { headers: getHeaders() };
        const url = AuthApiRoutes.CREATE_INVITATIONS;
        const body = invitations;
        dispatch(creatingInvitations());
        axios.post(url, body, headers)
            .then((response) => {
                const responses = response.data.map(item => {
                    return {
                        statusCode: item.statusCode,
                        id: item.content.id
                    }
                });
                dispatch(creatingInvitationsSuccess(responses));
            })
            .catch(error => {
                const err = getApiError(error);
                dispatch(creatingInvitationsFailed(err));
            })
    };
}

function fetchInvitation(invitationId) {
    return dispatch => {
        const url = `${AuthApiRoutes.INVITATIONS}/${invitationId}`;
        dispatch(fetchingInvitation());
        axios.get(url)
            .then((response) => {
                dispatch(fetchInvitationSuccess(response));
            })
            .catch(error => {
                dispatch(fetchInvitationFailed(error));
            });
    };
}

function reinviteUser(invitationId) {
    return dispatch => {
        const headers = { headers: getHeaders() };
        const url = `${AuthApiRoutes.INVITATIONS}/${invitationId}/reinvite`;

        dispatch(reinvitingUser());

        axios
            .patch(url, null, headers)
            .then((response) => {
                dispatch(
                    reinviteUserSuccess({
                        status: response.status, data: response.data, isReinviteResponse: true
                    })
                );
            })
            .catch(error => {
                let err = getApiError(error);
                err.isReinviteError = true;
                dispatch(reinviteUserFailed(err));
            });
    };
}


function bulkReinviteUsers(invitationIds) {
    return dispatch => {
        const headers = { headers: getHeaders() };
        const url = AuthApiRoutes.BULK_REINVITE_USERS;
        const body = invitationIds;
        dispatch(bulkReinvitingUsers());
        axios
            .post(url, body, headers)
            .then((response) => {
                dispatch(
                    bulkReinvitingUsersSuccess(response)
                );
            })
            .catch(error => {
                const err = getApiError(error);
                dispatch(bulkReinvitingUsersFailed(err));
            });
    };
}

function approveInvitation(invitationId) {
    return dispatch => {
        const headers = { headers: getHeaders() };
        const url = `${AuthApiRoutes.INVITATIONS}/${invitationId}/approve`;

        dispatch(approvingInvitation());

        axios.post(url, null, headers)
            .then((response) => {
                dispatch(approvingInvitationSuccess(response));
            })
            .catch(error => {
                const err = getApiError(error);
                dispatch(approvingInvitationFailed(err));
            })
    };
}

function bulkApproveInvitation(invitations) {
    return dispatch => {
        const headers = { headers: getHeaders() };
        const url = `${AuthApiRoutes.BULK_APPROVE_INVITATIONS}`;
        const body = invitations;

        dispatch(bulkApprovingInvitations());

        axios.post(url, body, headers)
            .then((response) => {
                dispatch(
                    bulkApproveInvitationsSuccess({
                        status: response.status, data: response.data, isBulkApproveResponse: true
                    })
                );
            })
            .catch(error => {
                let err = getApiError(error);
                err.isBulkApproveError = true;
                dispatch(bulkApproveInvitationsFailed(err));
            });
    };
}

function removeUserFromClient(userId, clientId) {
    return dispatch => {
        const headers = getHeaders();
        const url = `${AuthApiRoutes.USERS}/${userId}/clients/${clientId}/apps/bo`;
        dispatch(removingUser());
        axios.delete(url, { headers })
            .then(response => dispatch(removingUserSuccess(response)))
            .catch(error => dispatch(removingUserFailed(error)));
    }
}

function setInactiveDate(isActivating, userId, clientId) {
    return dispatch => {
        const headers = { headers: getHeaders() };
        const url = `${AuthApiRoutes.USERS}/${userId}/clients/${clientId}/inactivate`;
        let inactiveDate = new Date();
        const offset = inactiveDate.getTimezoneOffset();
        inactiveDate = isActivating
            ? null
            : new Date(inactiveDate.getTime() - (offset * 60 * 1000)).toISOString().split('T')[0];
        const body = { inactiveDate };

        dispatch(settingInactiveDate());

        axios.post(url, body, headers)
            .then((response) => {
                dispatch(setInactiveDateSuccess({ userId, inactiveDate, clientId, response }));
            })
            .catch(error => {
                let err = getApiError(error);
                err.isSetInactiveDateError = true;
                dispatch(setInactiveDateFailure(err));
            });
    };
}

function updateInvitation(invitationId, invitation) {
    return dispatch => {
        const headers = { headers: getHeaders() };
        const url = `${AuthApiRoutes.INVITATIONS}/${invitationId}`;
        const body = invitation;

        dispatch(updatingInvitation);

        axios.patch(url, body, headers)
            .then((response) => {
                dispatch(updateInvitationSuccess(response.data))
            })
            .catch(error => {
                let err = getApiError(error);
                err.isUpdateInvitationError = true;
                dispatch(updateInvitationFailure(err));
            });
    };
}